import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHeart } from 'react-icons/fa';

function Header() {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();

  const toggleMenu = () => setIsOpen(!isOpen);
  const toggleLangMenu = () => setIsLangOpen(!isLangOpen);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false);
    setIsOpen(false);
  };

  // Sticky shadow
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', label: t('header.home') },
    { to: '/about', label: t('header.about') },
    { to: '/ministries', label: t('header.ministries') },
    { to: '/events', label: t('header.events') },
    { to: '/contact', label: t('header.contact') },
    { to: '/prayer-request', label: t('header.prayerRequest') },
    { to: '/testimonials', label: t('header.testimonials') },
  ];

  return (
    <>
      {/* Skip link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:bg-accent focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        {t('header.skipNav')}
      </a>

      <nav
        className={`sticky top-0 z-50 bg-primary text-white transition-all duration-300 ${
          isScrolled ? 'shadow-xl py-2' : 'shadow-md py-3'
        }`}
      >
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-accent rounded-lg"
            aria-label="NAOGM Home"
          >
            <img
              src="/assets/images/logo.png"
              alt="NAOGM Logo"
              className="h-10 md:h-12 w-auto"
              onError={(e) =>
                (e.currentTarget.src = 'https://via.placeholder.com/150x50?text=NAOGM')
              }
            />
            <span className="font-bold text-lg tracking-wide hidden sm:block">
              NAOGM
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.to
                    ? 'text-accent bg-white/10'
                    : 'text-white/90 hover:text-accent hover:bg-white/5'
                }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Donate Button */}
            <Link
              to="/donate"
              className="ml-3 flex items-center gap-2 bg-accent hover:bg-yellow-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-300 hover:scale-105"
            >
              <FaHeart className="w-3.5 h-3.5" />
              {t('header.donate')}
            </Link>

            {/* Language Selector */}
            <div className="relative ml-2" ref={langRef}>
              <button
                onClick={toggleLangMenu}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/30 hover:bg-white/10 transition-colors text-sm font-medium"
                aria-expanded={isLangOpen}
              >
                {i18n.language.toUpperCase()}
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-primary border border-white/20 rounded-xl shadow-xl overflow-hidden z-50">
                  {[
                    { code: 'en', label: 'English' },
                    { code: 'sw', label: 'Swahili' },
                    { code: 'fr', label: 'French' },
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-accent/20 transition-colors ${
                        i18n.language === lang.code ? 'bg-white/10 text-accent' : ''
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-accent"
            onClick={toggleMenu}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 pt-3 pb-6 space-y-1 border-t border-white/10">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsOpen(false)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === item.to
                    ? 'bg-white/10 text-accent'
                    : 'text-white/90 hover:bg-white/5'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <Link
              to="/donate"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center gap-2 mt-3 bg-accent hover:bg-yellow-500 text-white font-semibold px-4 py-3 rounded-lg transition-colors"
            >
              <FaHeart />
              {t('header.donate')}
            </Link>

            {/* Mobile Language */}
            <div className="pt-3">
              <p className="px-4 text-xs text-white/60 mb-2 uppercase tracking-wider">Language</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { code: 'en', label: 'EN' },
                  { code: 'sw', label: 'SW' },
                  { code: 'fr', label: 'FR' },
                ].map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-accent text-white'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Header;